// The marking layer — what a user says draws them to a holding.
//
// One row per (user, symbol) in `holding_segments` (migrations/sprint5.sql).
// The answer is a pick from the company's OWN business-segment breakdown
// (company_segments, from its annual report), or an explicit "not sure yet".
// There is no free text anywhere on this path: the choices are rows the
// company filed, and the row stored points back at one of them by name.
//
// The mark snapshots the segment's revenue share, the fiscal year and the
// filing URL at the moment it was made. "How the share changed since you
// marked it" is then a comparison of two sourced facts, never a recomputation
// from whatever the table happens to hold today.
//
// State lives in a module store with a subscribe hook, the same shape as
// fxRates.js: the portfolio screen, the geo list, the home banner and the
// holding card all read the same map without threading it through App.

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { groupSegments } from "./companyProfile";

export const NOT_SURE = "__not_sure__";

// ── Choices ──────────────────────────────────────────────────────────────────
// Latest-year business segments with a share of the total they add up to.
//
// companyProfile.groupSegments deliberately computes no share, because some
// filings list a subtotal beside its parts and a sum over them double-counts.
// Here a share IS required — it is the fact the user is marking — so the
// subtotal problem is handled rather than avoided: a row is dropped from the
// denominator when its name says total, or when its revenue equals the sum of
// the other rows within 1%. What remains is labelled "of the revenue the
// company broke down", which is exactly what the number is.
//
// Returns { items: [{ name, revenue, pct, fiscalYear, sourceUrl }], fiscalYear,
//           sourceUrl, dropped: [names] } — or null when there is nothing to
// choose from (no business breakdown for this company).
const TOTAL_RE = /^(total|consolidated|all segments)\b/i;

export function segmentChoices(segments) {
  if (!Array.isArray(segments) || !segments.length) return null;
  const g = groupSegments(segments, "business");
  if (!g.items.length) return null;

  const rows = g.items.filter(i => i.revenue > 0);
  if (!rows.length) return null;

  const grand = rows.reduce((a, r) => a + r.revenue, 0);
  const dropped = [];
  const kept = rows.filter(r => {
    if (TOTAL_RE.test(r.name)) { dropped.push(r.name); return false; }
    const others = grand - r.revenue;
    if (others > 0 && Math.abs(r.revenue - others) / others < 0.01) { dropped.push(r.name); return false; }
    return true;
  });
  if (!kept.length) return null;

  const denom = kept.reduce((a, r) => a + r.revenue, 0);
  const fiscalYear = g.years[0];
  // The filing behind the latest year — every kept row came from it.
  const src = segments.find(s => s.segment_type === "business" && s.fiscal_year === fiscalYear && s.source_url);
  const sourceUrl = src ? src.source_url : null;
  if (!sourceUrl) return null;   // a choice without a filing behind it is not offered

  const items = kept
    .map(r => ({
      name: r.name,
      revenue: r.revenue,
      pct: Math.round((r.revenue / denom) * 1000) / 10,
      fiscalYear,
      sourceUrl,
    }))
    .sort((a, b) => b.pct - a.pct);

  return { items, fiscalYear, sourceUrl, dropped };
}

// The marked segment's share today, from live rows. null when the company no
// longer reports that segment (or reports no business split at all) — a fact
// the card states in those words rather than reusing the marked number.
export function currentShare(mark, segments) {
  if (!mark || !mark.segment_name) return null;
  const c = segmentChoices(segments);
  if (!c) return null;
  const hit = c.items.find(i => i.name === mark.segment_name);
  return hit ? { pct: hit.pct, fiscalYear: hit.fiscalYear, sourceUrl: hit.sourceUrl } : null;
}

// ── Store ────────────────────────────────────────────────────────────────────
// marks: { [SYMBOL]: row } where row.segment_name === null means "not sure".
let marks = {};
let loaded = false;
let loadedFor = null;
const subscribers = new Set();

function emit() {
  for (const fn of subscribers) fn({ marks, loaded });
}

const COLS = "symbol, cik, segment_type, segment_name, marked_pct, marked_fiscal_year, marked_source_url, created_at, updated_at";

function toUi(r) {
  return {
    symbol:           r.symbol,
    cik:              r.cik,
    segment_type:     r.segment_type,
    segment_name:     r.segment_name,
    marked_pct:       r.marked_pct == null ? null : Number(r.marked_pct),
    marked_fiscal_year: r.marked_fiscal_year,
    marked_source_url: r.marked_source_url,
    created_at:       r.created_at,
    updated_at:       r.updated_at,
  };
}

// Load (or reload) the signed-in user's marks. RLS returns only their rows, so
// no user filter is needed on the query; the id is kept to notice a switch.
export async function loadHoldingMarks(userId) {
  if (!supabase || !userId) { marks = {}; loaded = true; loadedFor = null; emit(); return; }
  if (loaded && loadedFor === userId) return;
  const { data, error } = await supabase.from("holding_segments").select(COLS);
  if (error) {
    // Table may not exist yet (migration pending) — degrade to "nothing marked".
    console.warn("[holdingSegments] load failed:", error.code, error.message);
    marks = {};
  } else {
    marks = Object.fromEntries((data || []).map(r => [r.symbol, toUi(r)]));
  }
  loaded = true;
  loadedFor = userId;
  emit();
}

export function resetHoldingMarks() {
  marks = {}; loaded = false; loadedFor = null; emit();
}

// Save a pick. `choice` is an item from segmentChoices(), plus the company's
// cik; NOT_SURE stores the all-null row the schema defines for it.
// The signed-in user's id: the one the store loaded for, or the live session.
async function currentUserId() {
  if (loadedFor) return loadedFor;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id || null;
  } catch { return null; }
}

export async function saveHoldingMark(symbol, choice, cik) {
  if (!supabase) return null;
  // holding_segments.user_id has no default (unlike app_events), so the row
  // names its owner explicitly; RLS then checks it equals auth.uid().
  const userId = await currentUserId();
  if (!userId) { console.error("[holdingSegments] save failed: no session"); return null; }
  const sym = String(symbol || "").toUpperCase();
  const row = choice === NOT_SURE
    ? { user_id: userId, symbol: sym, cik: null, segment_type: null, segment_name: null, marked_pct: null, marked_fiscal_year: null, marked_source_url: null, updated_at: new Date().toISOString() }
    : {
        user_id: userId, symbol: sym, cik, segment_type: "business", segment_name: choice.name,
        marked_pct: choice.pct, marked_fiscal_year: choice.fiscalYear, marked_source_url: choice.sourceUrl,
        updated_at: new Date().toISOString(),
      };
  // onConflict on the (user_id, symbol) primary key.
  const { data, error } = await supabase
    .from("holding_segments")
    .upsert(row, { onConflict: "user_id,symbol" })
    .select(COLS)
    .single();
  if (error) {
    console.error("[holdingSegments] save failed:", error.code, error.message);
    return null;
  }
  marks = { ...marks, [sym]: toUi(data) };
  emit();
  return marks[sym];
}

// A sold holding takes its mark with it.
export async function removeHoldingMark(symbol) {
  if (!supabase) return;
  const sym = String(symbol || "").toUpperCase();
  const { error } = await supabase.from("holding_segments").delete().eq("symbol", sym);
  if (error) console.error("[holdingSegments] remove failed:", error.code, error.message);
  const next = { ...marks }; delete next[sym];
  marks = next;
  emit();
}

export function useHoldingMarks() {
  const [snap, setSnap] = useState({ marks, loaded });
  useEffect(() => {
    subscribers.add(setSnap);
    setSnap({ marks, loaded });
    return () => { subscribers.delete(setSnap); };
  }, []);
  return snap;
}

// True when the user has answered for this symbol — marked or "not sure".
export function isAnswered(marksMap, symbol) {
  return !!marksMap && Object.prototype.hasOwnProperty.call(marksMap, String(symbol || "").toUpperCase());
}

// A real pick (not "not sure") for this symbol, or null.
export function markFor(marksMap, symbol) {
  const m = marksMap ? marksMap[String(symbol || "").toUpperCase()] : null;
  return m && m.segment_name ? m : null;
}

// One sentence for the geo card's second line. Facts only: what was marked
// and the share it had in the filing it was marked from.
export function markLine(mark) {
  if (!mark || !mark.segment_name) return null;
  return { name: mark.segment_name, pct: mark.marked_pct, fiscalYear: mark.marked_fiscal_year };
}
